import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Facebook, Instagram, Mail, MessageSquare, Phone, Send } from "lucide-react";
import { TikTokIcon } from "@/components/icons/TikTokIcon";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Contact = () => {
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Vui lòng nhập nội dung phản hồi");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.from("feedback").insert({
        message,
        user_id: session?.user?.id,
        email: session?.user?.email
      });

      if (error) throw error;
      
      toast.success("Cảm ơn bạn đã gửi phản hồi!");
      setMessage("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Có lỗi xảy ra. Vui lòng thử lại sau.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    {
      icon: Facebook,
      label: "Facebook",
      href: "https://www.facebook.com/maitechcorp",
    },
    {
      icon: Instagram,
      label: "Instagram",
      href: "https://www.instagram.com/maibot.official/reels/",
    },
    {
      icon: TikTokIcon,
      label: "TikTok",
      href: "https://www.tiktok.com/@maibot.official",
    },
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "maitechnologies1@gmail.com",
      href: "mailto:maitechnologies1@gmail.com",
    },
    {
      icon: Phone,
      label: "Điện thoại",
      value: "0777900721",
      href: "tel:0777900721",
    },
  ];

  return (
    <div className="container max-w-4xl py-8 space-y-8">
      <h1 className="text-3xl font-bold">Liên hệ và Phản hồi</h1>
      
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-6 space-y-6">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Gửi phản hồi
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="message">Nội dung phản hồi</Label>
              <Textarea
                id="message"
                placeholder="Nhập nội dung phản hồi của bạn..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              <Send className="w-4 h-4 mr-2" />
              Gửi phản hồi
            </Button>
          </form>
        </Card>

        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Mạng xã hội</h2>
            <div className="space-y-3">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <link.icon className="w-5 h-5" />
                  <span>{link.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Thông tin liên hệ</h2>
            <div className="space-y-3">
              {contactInfo.map((info) => (
                <a
                  key={info.label}
                  href={info.href}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <info.icon className="w-5 h-5" />
                  <div className="flex flex-col">
                    <span className="font-medium">{info.label}</span>
                    <span className="text-sm text-muted-foreground">
                      {info.value}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Contact;